import './index.css'
import { containsMergeTags } from '@disco/content-editor/default'
import {
  campaignDetailsSave,
  campaignDetailsSaveIfDirty,
  campaignTestSend,
} from '@disco/state-management/actions/campaign-details-actions'
import {
  deleteSection,
  duplicateSection,
  saveTemplateContent,
  switchToEditMode,
  updateCampaignEditorSection,
} from '@disco/state-management/actions/campaign-editor-actions'
import {
  createCampaignFooter,
  updateCampaignFooter,
} from '@disco/state-management/actions/campaign-footer-actions'
import {
  getCampaign,
  getCampaignEditedSection,
} from '@disco/state-management/selectors/campaigns'
import { getFirstContactFromCampaign } from '@disco/state-management/selectors/contacts-all'
import {
  getBusinessSettings,
  getUser,
} from '@disco/state-management/selectors/user'
import { isCampaignLoaded } from '@disco/utils/emailCampaigns'
import * as React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction } from 'react-router-dom'
import type { EmailCampaign } from 'types/campaign'
import type {
  Campaign,
  CampaignData,
  CampaignSection,
} from 'types/content-editor'
import type { State } from 'types/redux'
import type { User } from 'types/user'
import DocumentHeadTags from 'components/document-head-tags'
import AdminTool from 'components/ui/admin-tool'
import Button from 'components/ui/button'
import PageContainer from 'components/ui/page-container'
import CampaignHeader from 'pages/campaigns/campaign-header'
import CampaignNav, { EDIT_CONTENT_TAB } from 'pages/campaigns/campaign-nav'
import EditScheduledEmailModal from 'pages/campaigns/edit-scheduled-email-modal'
import ContentEditor from './content-editor'
import ContentEditorBlockSettings from './content-editor-block-settings'
import ContentEditorDebugButton from './content-editor-debug-button'
import ContentEditorSidebar from './content-editor-sidebar'
import TestEmailDialog from './test-email-dialog'
import type { RootState } from 'store/configureStore'
interface OwnProps {
  params: {
    campaignId: string
  }
  navigate: NavigateFunction
}

function mapStateToProps(state: RootState, props: OwnProps) {
  const campaignEntity = getCampaign(state, {
    campaignId: parseInt(props.params.campaignId),
  })
  let campaignSending = false

  if (campaignEntity) {
    campaignSending = campaignEntity.sending
  }

  const campaignEditor = state.emailCampaigns.campaignEditor
  // const ids = getCampaignPlaylistIds(campaignEditor.sections)
  const businessSettings = getBusinessSettings(state)
  const campaignId = parseInt(props.params.campaignId)
  const campaignData = {
    isEditor: true,
    hostname: `https://${businessSettings.hostname}`,
    campaignId,
    playlists: campaignEntity.playlists
      ? Object.keys(campaignEntity.playlists).map(k =>
          campaignEntity.playlists
            ? campaignEntity.playlists[parseInt(k, 10)]
            : null,
        )
      : [],
  }
  return {
    emailCampaign: campaignEntity,
    campaign: campaignEditor,
    sending: campaignSending,
    campaignData,
    sendAllowed: isCampaignLoaded(campaignEditor, campaignEntity.playlists),
    user: getUser(state),
    randomRecipient: getFirstContactFromCampaign(state, {
      campaignId,
    }),
    ...getCampaignEditedSection(state),
  }
}

const mapDispatchToProps = {
  campaignTestSend,
  campaignDetailsSave,
  campaignDetailsSaveIfDirty,
  duplicateSection,
  deleteSection,
  switchToEditMode,
  saveTemplateContent,
  updateCampaignEditorSection,
  updateCampaignFooter,
  createCampaignFooter,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
type ContentEditorPageProps = PropsFromRedux & OwnProps

function hasSeveralEmails(email: string): boolean {
  let match = email.match(/@/g)
  if (!match) return false
  return match.length > 1
}

class ContentEditorPage extends React.Component<
  ContentEditorPageProps,
  {
    testModal:
      | {
          sending: boolean
          errorMessage: string | null | undefined
        }
      | null
      | undefined
  }
> {
  state = {
    testModal: null,
  }
  _onDeleteSection = (index: number) => this.props.deleteSection(index)
  _onDuplicateSection = (index: number) => this.props.duplicateSection(index)
  _onEditSection = (index: number) => this.props.switchToEditMode(index)
  _handleTestEmailPrompt = () => {
    this.setState({
      testModal: {
        sending: false,
        errorMessage: null,
      },
    })
  }
  _handleTestEmailConfirm = async (email, mockData) => {
    const { campaignId } = this.props.params
    if (!campaignId) return

    try {
      if (hasSeveralEmails(email)) {
        const msg =
          'Sorry! Our test send function only supports sending to one email at a time.'
        this.setState({
          testModal: {
            sending: false,
            errorMessage: msg,
          },
        })
        return
      }

      this.setState({
        testModal: {
          sending: true,
          errorMessage: null,
        },
      })
      await this.props.campaignTestSend(parseInt(campaignId), email, mockData)
      this.setState({
        testModal: null,
      })
    } catch (err) {
      let msg

      if (err.body && err.body.errors) {
        msg = err.body.errors
        this.setState({
          testModal: {
            sending: false,
            errorMessage: msg,
          },
        })
      } else {
        console.error(err)
        this.setState({
          testModal: {
            sending: false,
            errorMessage: null,
          },
        })
      }
    }
  }
  _handleCancel = () => {
    this.setState({
      testModal: null,
    })
  }
  _handleNextStep = () => {
    const { campaignId } = this.props.params
    if (!campaignId) return
    // performSave notifies then rethrows; nothing here can act on it
    if (this.props.emailCampaign.status !== 'sent')
      Promise.resolve(
        this.props.campaignDetailsSave(parseInt(campaignId), true),
      ).catch(() => {})
    this.props.navigate(`/campaigns/${campaignId}/confirm`)
  }

  componentWillUnmount() {
    // editor edits wait on the autosave timer, so leaving would otherwise drop them
    const { campaignId } = this.props.params
    if (!campaignId) return
    this.props.campaignDetailsSaveIfDirty(parseInt(campaignId))
  }

  componentDidUpdate() {
    const { campaignId } = this.props.params
    const { emailCampaign } = this.props
    if (!campaignId) return

    if (!emailCampaign?.template_data?.template_content_saved) {
      this.props.saveTemplateContent(Number(campaignId))
    }
  }

  render() {
    const showSampleDataCheckbox = containsMergeTags(this.props.campaign)
    let testModal

    if (this.state.testModal) {
      testModal = (
        <TestEmailDialog
          showSampleDataCheckbox={showSampleDataCheckbox}
          testRecipient={this.props.randomRecipient}
          modalState={this.state.testModal}
          onSubmit={this._handleTestEmailConfirm}
          onCancel={this._handleCancel}
        />
      )
    }

    const sendTestBtn = (
      <div className="content-editor__test-button">
        <Button
          caption="Send test email"
          onClick={this._handleTestEmailPrompt}
          disabled={!this.props.sendAllowed}
        />
      </div>
    )
    return (
      <div className="Page_Content">
        <DocumentHeadTags
          title="Edit content | DISCO"
          link={{
            href: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap',
            rel: 'stylesheet',
          }}
        />
        {testModal}
        <CampaignHeader
          campaignId={this.props.params.campaignId}
          onNextClick={this._handleNextStep}
        />
        <PageContainer>
          <CampaignNav
            campaignId={this.props.params.campaignId}
            activeStep={EDIT_CONTENT_TAB}
          />
          <PageContainer.Section>
            <div className="content-editor">
              <div className="content-editor__page">
                <ContentEditor
                  className="email-content"
                  key={this.props.params.campaignId}
                  campaignId={this.props.params.campaignId}
                  campaignData={this.props.campaignData}
                  campaign={this.props.campaign}
                  onDeleteSection={this._onDeleteSection}
                  onDuplicateSection={this._onDuplicateSection}
                  onEditSection={this._onEditSection}
                  user={this.props.user}
                />
              </div>
              <div>
                {sendTestBtn}
                <div className="content-editor__sidebar">
                  {!this.props.section && <ContentEditorSidebar />}
                  <ContentEditorBlockSettings />
                </div>
              </div>
              <AdminTool
                renderFn={() => (
                  <ContentEditorDebugButton
                    campaign={this.props.campaign}
                    campaignData={this.props.campaignData}
                  />
                )}
              />
            </div>
          </PageContainer.Section>
          <EditScheduledEmailModal campaignId={this.props.params.campaignId} />
        </PageContainer>
      </div>
    )
  }
}

const ConnectedContentEditorPage = connector(ContentEditorPage)

// Wrapper component to provide router params and navigation to the class component
const CampaignContentEditorPageWithRouter = (props: any) => {
  const params = useParams<{ campaignId: string }>()
  const navigate = useNavigate()

  return (
    <ConnectedContentEditorPage
      {...props}
      params={params}
      navigate={navigate}
    />
  )
}

export default CampaignContentEditorPageWithRouter
